/* @flow */

import type { NETGEM_API_PLAYINGHISTORY, NETGEM_API_PLAYINGHISTORY_ITEM } from '../../../../libs/netgemLibrary/v8/types/PlayingHistory';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../../libs/netgemLibrary/v8/types/RequestResponse';
import type { CombinedReducers } from '../../../reducers';
import type { Dispatch } from '../../../types/types';
import type { RequestResponseMethodDefinitionType } from '../emitter';

import { isUndefinedOrNull } from '../../../../helpers/jsHelpers/types';
import Localizer from '../../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../../helpers/messenger';
import ProfilesManager from '../../../../helpers/profiles/profilesManager';
import { CustomNetworkError } from '../../../../libs/netgemLibrary/helpers/CustomNetworkError';
import { PersonalDataKey } from '../../../../libs/netgemLibrary/personalData/constants/keys';
import { HttpStatus } from '../../../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { deletePlayingHistoryItem, deletePlayingHistoryItemETag, resetPlayingHistory, setPlayingHistoryError, updatePlayingHistoryItem, updateWholePlayingHistory } from '../../../ui/actions';
import { sendPersonalDataListDeleteRequest } from './delete';
import { sendPersonalDataListGetRequest } from './get';
import { sendPersonalDataListPostRequest } from './post';

const getPlayingHistory: (signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<void> => {
    const {
      ui: {
        playingHistoryETagCache: { playingHistory: initialETag },
      },
    } = getState();

    const playingHistoryKey = ProfilesManager.buildCurrentProfileKey(PersonalDataKey.PlayingHistory);

    return dispatch(sendPersonalDataListGetRequest(playingHistoryKey, undefined, initialETag, signal))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        const { eTag: playingHistoryETag } = data;
        const result = JSON.parse(data.result as string);

        // Filter out malformed items
        const playingHistory: NETGEM_API_PLAYINGHISTORY = validatePlayingHistory(result as NETGEM_API_PLAYINGHISTORY);

        dispatch(updateWholePlayingHistory(playingHistory, playingHistoryETag));
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (status === HttpStatus.NotFound) {
          // Not found means playing history does not exist yet
          dispatch(resetPlayingHistory());
          return;
        } else if (status === HttpStatus.NotModified) {
          return;
        }

        dispatch(setPlayingHistoryError());
        throw error;
      });
  };

const validatePlayingHistory: (playingHistory: NETGEM_API_PLAYINGHISTORY) => NETGEM_API_PLAYINGHISTORY = (playingHistory) => {
  if (isUndefinedOrNull(playingHistory)) {
    return {};
  }

  const validatedPlayingHistory: NETGEM_API_PLAYINGHISTORY = {};

  Object.keys(playingHistory).forEach((key) => {
    if (typeof key === 'string' && key !== '') {
      const { channelId, locationId, profiles } = playingHistory[key];

      if (typeof channelId === 'string' && typeof locationId === 'string' && typeof profiles === 'object') {
        const validatedPlayingHistoryItem: NETGEM_API_PLAYINGHISTORY_ITEM = { channelId, locationId, profiles: {} };

        Object.entries(profiles).forEach(([pKey, pValue]) => {
          if (typeof pKey === 'string' && pKey !== '' && typeof pValue?.date === 'string' && !isNaN(Date.parse(pValue.date))) {
            validatedPlayingHistoryItem.profiles[pKey] = pValue;
          }
        });

        if (Object.keys(validatedPlayingHistoryItem.profiles).length > 0) {
          validatedPlayingHistory[key] = validatedPlayingHistoryItem;
        }
      }
    }
  });

  return validatedPlayingHistory;
};

const updatePlayingHistory: (item: NETGEM_API_PLAYINGHISTORY_ITEM, itemId: string, signal?: AbortSignal, isSecondAttempt?: boolean) => RequestResponseMethodDefinitionType =
  (item, itemId, signal, isSecondAttempt) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<void> => {
    const {
      ui: { playingHistory, playingHistoryETagCache },
    } = getState();
    const { [itemId]: existingItem } = playingHistory || {};
    const { [itemId]: existingItemETag } = playingHistoryETagCache;

    const playingHistoryKey = ProfilesManager.buildCurrentProfileKey(PersonalDataKey.PlayingHistory);

    if (isSecondAttempt || (existingItem && !existingItemETag)) {
      // Item is already in playing history but ETag is missing (i.e. item was saved during a previous session): a GET is required to retrieve the associated ETag
      return dispatch(sendPersonalDataListGetRequest(playingHistoryKey, itemId, existingItemETag, signal))
        .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
          const { eTag } = data;
          return dispatch(updatePlayingHistoryWithETag(item, itemId, eTag, signal, isSecondAttempt));
        })
        .catch((error) => {
          // GET request failed: nothing to update
          Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('settings.content.playing_history.update_error'));
          throw error;
        });
    }

    // Item and associated ETag exist (i.e. item saved during current session) or none of them exists (i.e. item never saved)
    return dispatch(updatePlayingHistoryWithETag(item, itemId, existingItemETag, signal, isSecondAttempt));
  };

const updatePlayingHistoryWithETag: (item: NETGEM_API_PLAYINGHISTORY_ITEM, itemId: string, eTag: ?string, signal?: AbortSignal, isSecondAttempt?: boolean) => RequestResponseMethodDefinitionType =
  (item, itemId, eTag, signal, isSecondAttempt) =>
  (dispatch: Dispatch): Promise<void> =>
    dispatch(sendPersonalDataListPostRequest(ProfilesManager.buildCurrentProfileKey(PersonalDataKey.PlayingHistory), itemId, item, eTag, signal))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        const { eTag: itemETag } = data;

        if (itemETag) {
          dispatch(updatePlayingHistoryItem(item, itemId, itemETag));
        }
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (!isSecondAttempt && (status === HttpStatus.Conflict || status === HttpStatus.PreconditionFailed || status === HttpStatus.PreconditionRequired)) {
          /*
           * Most probable cause: another device paired with the same account already updated the playing history
           * So we send a GET request to retrieve the latest ETag, but we only try once to avoid infinite loop between multiple devices
           */
          dispatch(deletePlayingHistoryItemETag(itemId));
          return dispatch(updatePlayingHistory(item, itemId, signal, true));
        }

        throw error;
      });

const deleteWholePlayingHistory: (profileId?: string, signal?: AbortSignal) => RequestResponseMethodDefinitionType = (profileId, signal) => deletePlayingHistory(profileId, null, signal);

const deletePlayingHistory: (profileId?: string, itemId: ?string, signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (profileId, itemId, signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<void> => {
    const {
      ui: {
        playingHistoryETagCache,
        playingHistoryETagCache: { playingHistory: initialListETag },
      },
    } = getState();
    const initialItemETag = itemId ? playingHistoryETagCache[itemId] : null;
    const initialETag = itemId ? initialItemETag : initialListETag;

    const playingHistoryKey = ProfilesManager.buildProfileKey(PersonalDataKey.PlayingHistory, profileId);

    // Get ETag
    return dispatch(sendPersonalDataListGetRequest(playingHistoryKey, itemId, initialETag, signal))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        // Delete
        const { eTag } = data;
        if (eTag) {
          return dispatch(deletePlayingHistoryWithETag(profileId, itemId, eTag, signal));
        }
      })
      .catch((eTagError: CustomNetworkError) => {
        const status = eTagError.getStatus();
        if (status === HttpStatus.NotFound) {
          // Playing history not found for this user: nothing to delete
          return;
        } else if (status === HttpStatus.NotModified) {
          // Playing history has not recently changed: current ETag should be used
          return dispatch(deletePlayingHistoryWithETag(profileId, itemId, initialETag, signal));
        }

        return Promise.reject(eTagError);
      });
  };

const deletePlayingHistoryWithETag: (profileId?: string, itemId: ?string, eTag: ?string, signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (profileId, itemId, eTag, signal) =>
  (dispatch: Dispatch): Promise<void> =>
    dispatch(sendPersonalDataListDeleteRequest(ProfilesManager.buildProfileKey(PersonalDataKey.PlayingHistory, profileId), itemId, eTag, signal)).then(() => {
      if (itemId) {
        // Delete item from list
        dispatch(deletePlayingHistoryItem(itemId));
        return;
      }

      // Clear whole list
      dispatch(resetPlayingHistory());
    });

export { deletePlayingHistory, deleteWholePlayingHistory, getPlayingHistory, updatePlayingHistory };
